import Highland from 'highland';
import { cellAddressToIndexes } from 'shared/excel/range-address';
import { findLastSequentialIndex } from './range-utils';
import { IndexedRow } from "shared/execution/workbook-api";

type ReadValuesOptions = {
  visibleOnly?: boolean;
  selectedOnly?: boolean;
  columnsToRead?: number[];
}

export class RangesValuesReader {

  constructor(
    private context: Excel.RequestContext,
    private readingBatchSize: number,
  ) {}

  private calcAvailableRowCount(rangeBuffer: Excel.Range[]) {
    const addedRowCount = rangeBuffer.reduce((sum, range) => sum + range.rowCount, 0);
    return this.readingBatchSize - addedRowCount;
  }

  private async getVisibleRanges(range: Excel.Range) {
    const visibleView = range.getColumn(0).getVisibleView();
    visibleView.load({cellAddresses: true});
    range.load({columnCount: true, rowIndex: true, columnIndex: true});
    await this.context.sync()

    if (visibleView.cellAddresses.length === 0 || visibleView.cellAddresses[0].length === 0) {
      return [];
    }

    const cellAddresses = visibleView.cellAddresses as string[][];
    const rowNumbers = cellAddresses.map(cellAddress => cellAddressToIndexes(cellAddress[0])![1]);

    let startIndex = 0;
    const result: Excel.Range[] = [];

    do {
      let endIndex = findLastSequentialIndex(rowNumbers, startIndex, (item) => item);

      const startRowNumber = rowNumbers[startIndex];
      const endRowNumber = rowNumbers[endIndex];

      const subRange = range
        .getRow(startRowNumber - range.rowIndex)
        .getAbsoluteResizedRange(endRowNumber - startRowNumber + 1, range.columnCount)
      result.push(subRange);

      startIndex = endIndex + 1;
    } while (startIndex < cellAddresses.length)

    return result;
  }

  private async addOnlyVisibleRowsToBuffer(
    range: Excel.Range,
    rangeBuffer: Excel.Range[],
  ) {
    const visibleRanges = await this.getVisibleRanges(range);
    visibleRanges.forEach(visibleRange => {
      visibleRange.load({rowCount: true, columnCount: true, address: true});
      rangeBuffer.push(visibleRange);
    });
    await this.context.sync();
  }

  private async addRangeToBuffer(
    range: Excel.Range,
    rangeBuffer: Excel.Range[],
    visibleOnly: boolean,
  ) {

    const loadRangeSizeProps = async (range: Excel.Range) => {
      range.load({rowCount: true, columnCount: true, address: true});
      await this.context.sync();
    }

    await loadRangeSizeProps(range);

    const availableRowCount = this.calcAvailableRowCount(rangeBuffer);

    let rangeToAdd: Excel.Range = range;
    let remainingRange: Excel.Range | null = null;

    if (range.rowCount > availableRowCount) {
      rangeToAdd = range.getAbsoluteResizedRange(availableRowCount, range.columnCount);
      await loadRangeSizeProps(rangeToAdd);
      remainingRange = range
        .getRow(rangeToAdd.rowCount)
        .getAbsoluteResizedRange(range.rowCount - availableRowCount, range.columnCount);
    }

    visibleOnly
      ? await this.addOnlyVisibleRowsToBuffer(rangeToAdd, rangeBuffer)
      : rangeBuffer.push(rangeToAdd);

    return remainingRange;
  }

  private splitBySelectedColumns(range: Excel.Range, columnsIndexes: number[]) {
    if (columnsIndexes.length === 0) {
      return [range];
    }

    const result: Excel.Range[] = [];
    let startIndex = 0;
    let endIndex = 0;

    do {
      endIndex = findLastSequentialIndex(columnsIndexes, startIndex, (item) => item);

      const columnsRange = range
        .getColumn(columnsIndexes[startIndex])
        .getResizedRange(0,  columnsIndexes[endIndex] - columnsIndexes[startIndex]);

      result.push(columnsRange);
      startIndex = endIndex + 1;
    } while(startIndex < columnsIndexes.length);

    return result;
  }

  private columnValuesToRows(columns: Excel.Range[]) {
    if (columns.length === 1) {
      return columns[0].values;
    }

    const values: any[] = [];
    columns.forEach((column, columnIndex) => {
      column.values.forEach((row, rowIndex) => {
        if (columnIndex === 0) {
          values.push([]);
        }
        values[rowIndex].push(...row);
      })
    })
    return values;
  }

  readValues(
    ranges: Excel.Range[],
    options: ReadValuesOptions = {
      visibleOnly: false,
    },
  ): Highland.Stream<IndexedRow> {
    let rangeIndex = 0;
    let remainingRange: Excel.Range | null = null;
    let rowBuffer: IndexedRow[] = [];
    let rowBufferIndex = 0;

    return Highland(async (push, next) => {

      const loadValuesFromExcel = async (ranges: Excel.Range[]) => {
        const { columnsToRead } = options;

        const columnRanges = ranges.map(range => {
          const columns = this.splitBySelectedColumns(range, columnsToRead || []);
          columns.forEach(columnRange => {
            columnRange.load({values: true, rowIndex: true})
          });
          return columns;
        });
        await this.context.sync();

        const result: any[][] = [];
        columnRanges.forEach(columns => {
          const rowValues = this.columnValuesToRows(columns);
          const rowIndex = columns[0].rowIndex;

          rowValues.forEach((row, index) => result.push([index + rowIndex, ...row]));
        })

        return result;
      }

      const readNextValues = async (): Promise<IndexedRow[]> => {
        const rangeBuffer: Excel.Range[] = [];

        while(rangeIndex < ranges.length) {
          const nextRange = remainingRange || ranges[rangeIndex];

          remainingRange = await this.addRangeToBuffer(nextRange, rangeBuffer, !!options.visibleOnly);
          if (!remainingRange) {
            rangeIndex++;
          }

          if (this.calcAvailableRowCount(rangeBuffer) <= 0) {
            return await loadValuesFromExcel(rangeBuffer) as IndexedRow[];
          }
        }

        if (rangeBuffer.length > 0) {
          return await loadValuesFromExcel(rangeBuffer) as IndexedRow[];
        }

        return [];
      }

      if (rowBufferIndex === rowBuffer.length) {
        rowBuffer = await readNextValues();
        rowBufferIndex = 0;
      }

      if (rowBuffer.length > 0) {
        push(null, rowBuffer[rowBufferIndex++]);
        next();
        return;
      }

      push(null, Highland.nil);
    })
  }
}
